import type { ChecklistTemplate } from '../domains/templates/types';
import type { OrderPriority, OrderStatus, VisitOutcome, WorkType } from '../domains/orders/types';

export const DEFAULT_COMPANY_ID = '00000000-0000-0000-0000-000000000001';
export const DEFAULT_COMPANY_SLUG = 'pilot-company';

export const APP_NAME = 'Кворум';
export const STORAGE_BUCKET = 'visit-media';

export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  maintenance: 'Техническое обслуживание',
  diagnostics: 'Диагностика',
  repair: 'Ремонт',
  installation: 'Монтаж',
  other: 'Другое',
};

export const PRIORITY_LABELS: Record<OrderPriority, string> = {
  low: 'Низкий',
  normal: 'Нормальный',
  high: 'Высокий',
  urgent: 'Срочный',
};

export const STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'Новая',
  assigned: 'Назначена',
  in_progress: 'В работе',
  // 1.19.4 — transit-state: техник нажал «Завершить», но финал ещё
  // не доехал на сервер (фото, подпись, items, invoice).
  pending_completion: 'Закрывается',
  done: 'Выполнена',
  failed: 'Проблема',
  cancelled: 'Отменена',
  awaiting_parts: 'Ждёт запчасти',
  awaiting_approval: 'Ждёт согласования',
  awaiting_follow_up: 'Требуется повторный выезд',
  rescheduled: 'Клиент перенёс',
  failed_no_access: 'Нет доступа',
};

export const VISIT_OUTCOME_LABELS: Record<VisitOutcome, string> = {
  completed: 'Выполнено полностью',
  partially_completed: 'Выполнено частично',
  follow_up_required: 'Требуется повторный выезд',
  waiting_approval: 'Ожидает согласования',
  no_access: 'Нет доступа на объект',
  client_rescheduled: 'Клиент перенёс визит',
  parts_required: 'Требуются запчасти / материалы',
  // 1.12.1 — для abortVisit. Слово «прерван» точно отражает суть: техник
  // на месте, начал визит, но дальше двигаться невозможно (клиент отказал
  // в продолжении, нерешаемая проблема). Отличается от no_access тем, что
  // контакт с объектом был.
  failed: 'Визит прерван',
};

/**
 * Short, actionable dispatcher hint for each exceptional outcome.
 * 'completed' is deliberately absent — it needs no follow-up.
 */
export const FOLLOW_UP_HINTS: Partial<Record<VisitOutcome, string>> = {
  partially_completed:  'Работа завершена частично — запланируйте повторный визит для завершения.',
  follow_up_required:   'Нужен повторный выезд — создайте новую заявку.',
  waiting_approval:     'Нужно согласование — уточните у клиента или руководства.',
  no_access:            'Нет доступа на объект — перепланируйте дату визита.',
  client_rescheduled:   'Клиент перенёс визит — согласуйте новую дату и создайте заявку.',
  parts_required:       'Нужны запчасти / материалы — закажите комплектующие и создайте повторный выезд.',
  // 1.12.1 — визит прерван техником посреди работы. Диспетчер должен
  // понять причину (см. outcome_reason) и решить — повторить или закрыть.
  failed:               'Визит был прерван — изучите причину и при необходимости создайте повторный выезд.',
};

/**
 * Set of outcomes that require active dispatcher follow-up.
 * All VisitOutcomes except 'completed'.
 */
export const EXCEPTIONAL_OUTCOMES = new Set<VisitOutcome>([
  'partially_completed',
  'follow_up_required',
  'waiting_approval',
  'no_access',
  'client_rescheduled',
  'parts_required',
  'failed',
]);

export const PILOT_TEMPLATE_IDS = {
  maintenance: 'template-maintenance-v1',
  diagnostics: 'template-diagnostics-v1',
  repair: 'template-repair-v1',
  installation: 'template-installation-v1',
} as const;

export const DEFAULT_TEMPLATE_ID = PILOT_TEMPLATE_IDS.maintenance;

const PILOT_CHECKLIST_TEMPLATES: ChecklistTemplate[] = [
  {
    id: PILOT_TEMPLATE_IDS.maintenance,
    company_id: DEFAULT_COMPANY_ID,
    name: 'ТО — плановое обслуживание',
    work_type: 'maintenance',
    is_active: true,
    items: [
      { id: 'maint-1', template_id: PILOT_TEMPLATE_IDS.maintenance, order_index: 1, title: 'Внешний осмотр оборудования', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'maint-2', template_id: PILOT_TEMPLATE_IDS.maintenance, order_index: 2, title: 'Состояние фильтров', type: 'select', options: ['Чистые', 'Промыты', 'Заменены', 'Требуют замены'], is_required: true, hint: null },
      { id: 'maint-3', template_id: PILOT_TEMPLATE_IDS.maintenance, order_index: 3, title: 'Дренаж и конденсат', type: 'select', options: ['Норма', 'Прочищено', 'Требуется дополнительная работа'], is_required: true, hint: null },
      { id: 'maint-4', template_id: PILOT_TEMPLATE_IDS.maintenance, order_index: 4, title: 'Очистка теплообменника выполнена', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'maint-5', template_id: PILOT_TEMPLATE_IDS.maintenance, order_index: 5, title: 'Температура подачи, °C', type: 'number', options: null, is_required: true, hint: 'Обычно 7–12°C' },
      { id: 'maint-6', template_id: PILOT_TEMPLATE_IDS.maintenance, order_index: 6, title: 'Итоговое состояние системы', type: 'select', options: ['Исправна', 'Нужна диагностика', 'Нужен ремонт'], is_required: true, hint: null },
      { id: 'maint-7', template_id: PILOT_TEMPLATE_IDS.maintenance, order_index: 7, title: 'Комментарий по обслуживанию', type: 'text', options: null, is_required: false, hint: 'Необязательно' },
    ],
  },
  {
    id: PILOT_TEMPLATE_IDS.diagnostics,
    company_id: DEFAULT_COMPANY_ID,
    name: 'Диагностика — поиск причины',
    work_type: 'diagnostics',
    is_active: true,
    items: [
      { id: 'diag-1', template_id: PILOT_TEMPLATE_IDS.diagnostics, order_index: 1, title: 'Подтверждение симптома на месте', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'diag-2', template_id: PILOT_TEMPLATE_IDS.diagnostics, order_index: 2, title: 'Основной симптом', type: 'select', options: ['Не охлаждает', 'Шум/вибрация', 'Протечка', 'Ошибка на пульте', 'Запах/грязь'], is_required: true, hint: null },
      { id: 'diag-3', template_id: PILOT_TEMPLATE_IDS.diagnostics, order_index: 3, title: 'Давление / электрические параметры', type: 'number', options: null, is_required: true, hint: 'Зафиксируйте ключевое измерение' },
      { id: 'diag-4', template_id: PILOT_TEMPLATE_IDS.diagnostics, order_index: 4, title: 'Вероятная причина', type: 'text', options: null, is_required: true, hint: 'Кратко и по делу' },
      { id: 'diag-5', template_id: PILOT_TEMPLATE_IDS.diagnostics, order_index: 5, title: 'Нужны дополнительные материалы / доступ', type: 'checkbox', options: null, is_required: false, hint: null },
      { id: 'diag-6', template_id: PILOT_TEMPLATE_IDS.diagnostics, order_index: 6, title: 'Рекомендованное следующее действие', type: 'select', options: ['Плановый ремонт', 'Срочный ремонт', 'Наблюдение', 'Перезапуск/сброс выполнен'], is_required: true, hint: null },
    ],
  },
  {
    id: PILOT_TEMPLATE_IDS.repair,
    company_id: DEFAULT_COMPANY_ID,
    name: 'Ремонт — устранение неисправности',
    work_type: 'repair',
    is_active: true,
    items: [
      { id: 'repair-1', template_id: PILOT_TEMPLATE_IDS.repair, order_index: 1, title: 'Неисправность подтверждена', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'repair-2', template_id: PILOT_TEMPLATE_IDS.repair, order_index: 2, title: 'Вид ремонта', type: 'select', options: ['Электрика', 'Фреоновый контур', 'Механика', 'Чистка/восстановление'], is_required: true, hint: null },
      { id: 'repair-3', template_id: PILOT_TEMPLATE_IDS.repair, order_index: 3, title: 'Заменённый / восстановленный узел', type: 'text', options: null, is_required: true, hint: null },
      { id: 'repair-4', template_id: PILOT_TEMPLATE_IDS.repair, order_index: 4, title: 'Контрольный запуск выполнен', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'repair-5', template_id: PILOT_TEMPLATE_IDS.repair, order_index: 5, title: 'Результат контрольного запуска', type: 'select', options: ['Исправно', 'Частично исправно', 'Требуется повторный выезд'], is_required: true, hint: null },
      { id: 'repair-6', template_id: PILOT_TEMPLATE_IDS.repair, order_index: 6, title: 'Дополнительные замечания', type: 'text', options: null, is_required: false, hint: 'Например, износ смежных узлов' },
    ],
  },
  {
    id: PILOT_TEMPLATE_IDS.installation,
    company_id: DEFAULT_COMPANY_ID,
    name: 'Монтаж — ввод в эксплуатацию',
    work_type: 'installation',
    is_active: true,
    items: [
      { id: 'install-1', template_id: PILOT_TEMPLATE_IDS.installation, order_index: 1, title: 'Место монтажа подготовлено', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'install-2', template_id: PILOT_TEMPLATE_IDS.installation, order_index: 2, title: 'Оборудование смонтировано', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'install-3', template_id: PILOT_TEMPLATE_IDS.installation, order_index: 3, title: 'Подключение питания и коммуникаций', type: 'select', options: ['Полностью', 'Частично', 'Ожидает подрядчика'], is_required: true, hint: null },
      { id: 'install-4', template_id: PILOT_TEMPLATE_IDS.installation, order_index: 4, title: 'Пусконаладка выполнена', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'install-5', template_id: PILOT_TEMPLATE_IDS.installation, order_index: 5, title: 'Инструктаж клиента', type: 'checkbox', options: null, is_required: true, hint: null },
      { id: 'install-6', template_id: PILOT_TEMPLATE_IDS.installation, order_index: 6, title: 'Комментарий по монтажу', type: 'text', options: null, is_required: false, hint: 'Что осталось на наблюдении' },
    ],
  },
];

function cloneTemplate(template: ChecklistTemplate): ChecklistTemplate {
  return {
    ...template,
    items: template.items.map((item) => ({
      ...item,
      options: item.options ? [...item.options] : null,
    })),
  };
}

export const DEFAULT_CHECKLIST_TEMPLATE = cloneTemplate(PILOT_CHECKLIST_TEMPLATES[0]);

export function getPilotChecklistTemplates(): ChecklistTemplate[] {
  return PILOT_CHECKLIST_TEMPLATES.map(cloneTemplate);
}

export function getSeedTemplateById(templateId: string | null | undefined): ChecklistTemplate | null {
  if (!templateId) return null;
  const template = PILOT_CHECKLIST_TEMPLATES.find((entry) => entry.id === templateId);
  return template ? cloneTemplate(template) : null;
}

export function getSeedTemplateByWorkType(workType: WorkType | null | undefined): ChecklistTemplate | null {
  if (!workType) return null;
  const template = PILOT_CHECKLIST_TEMPLATES.find((entry) => entry.work_type === workType);
  return template ? cloneTemplate(template) : null;
}

export function getDefaultTemplateForWorkType(workType: WorkType | null | undefined): ChecklistTemplate {
  return getSeedTemplateByWorkType(workType) ?? cloneTemplate(DEFAULT_CHECKLIST_TEMPLATE);
}
